import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, Loading } from "@/components/common/ui";
import { DataManager } from "@/components/settings/DataManager";
import { CategoryManager } from "@/components/settings/CategoryManager";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, X, Save, Trash2, KeyRound, UserPlus, Layers, GripVertical, Eye, EyeOff, LayoutGrid, RotateCcw, Search, Database, BellRing, LayoutDashboard, ShieldCheck, Lightbulb, Wallet } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin (full access)" },
  { value: "leader", label: "CEO / Leader (full access)" },
  { value: "manager", label: "Manager (assigned verticals)" },
  { value: "executive", label: "Executive (data entry)" },
];

/* -------- String list editor (chips) -------- */
function StringListEditor({ title, description, items, onChange, testid, note }) {
  const [val, setVal] = useState("");
  const [query, setQuery] = useState("");
  const list = items || [];
  const add = () => {
    const v = val.trim();
    if (!v || list.includes(v)) return;
    onChange([...list, v]);
    setVal("");
  };
  const q = query.trim().toLowerCase();
  const filtered = q ? list.filter((it) => String(it).toLowerCase().includes(q)) : list;
  return (
    <SectionCard
      title={title}
      description={description}
      action={<Badge variant="secondary" className="tabular-nums" data-testid={`${testid}-count`}>{list.length}</Badge>}
    >
      <div className="p-4">
        {note && <p className="mb-2 text-[11px] text-muted-foreground">{note}</p>}
        {list.length > 12 && (
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Filter ${title.toLowerCase()}…`} className="h-8 pl-8 text-sm" data-testid={`${testid}-filter`} />
          </div>
        )}
        <div className="flex max-h-52 flex-wrap gap-2 overflow-y-auto rounded-lg border bg-[hsl(var(--surface-2))] p-2" data-testid={`${testid}-chips`}>
          {filtered.map((it) => (
            <span key={it} className="inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-1 text-sm">
              {it}
              <button onClick={() => onChange(list.filter((x) => x !== it))} className="text-muted-foreground hover:text-[hsl(var(--danger))]" aria-label={`Remove ${it}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          {filtered.length === 0 && <span className="px-1 py-0.5 text-sm text-muted-foreground">{q ? "No items match your filter." : "No items yet."}</span>}
        </div>
        <div className="mt-3 flex gap-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} onBlur={add} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={`Add ${title.toLowerCase()}…`} className="h-9" data-testid={`${testid}-input`} />
          <Button variant="outline" onClick={add} className="h-9" data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></Button>
        </div>
      </div>
    </SectionCard>
  );
}

/* -------- Mapped list editor (name + mapping) -------- */
function MappedListEditor({ title, description, items, onChange, mapKey, mapLabel, options, testid }) {
  const [name, setName] = useState("");
  const [map, setMap] = useState(options[0] || "");
  const add = () => {
    const n = name.trim();
    if (!n || (items || []).some((i) => i.name === n)) return;
    onChange([...(items || []), { name: n, [mapKey]: map, short: n, tier: "primary" }]);
    setName("");
  };
  return (
    <SectionCard title={title} description={description}>
      <div className="p-4">
        <div className="max-h-72 overflow-y-auto rounded-lg border" data-testid={`${testid}-list`}>
          <table className="w-full border-collapse">
            <tbody>
              {(items || []).map((it, i) => (
                <tr key={it.name + i} className="border-b last:border-0">
                  <td className="px-3 py-2 text-sm">{it.name}</td>
                  <td className="px-3 py-2 w-48">
                    <Select value={it[mapKey]} onValueChange={(v) => onChange(items.map((x) => x.name === it.name ? { ...x, [mapKey]: v } : x))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 w-10 text-right">
                    <button onClick={() => onChange(items.filter((x) => x.name !== it.name))} className="text-muted-foreground hover:text-[hsl(var(--danger))]" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={add} placeholder={`New ${title.toLowerCase()} name…`} className="h-9 flex-1" data-testid={`${testid}-name`} />
          <Select value={map} onValueChange={setMap}>
            <SelectTrigger className="h-9 w-44" data-testid={`${testid}-map`}><SelectValue placeholder={mapLabel} /></SelectTrigger>
            <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={add} className="h-9" data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></Button>
        </div>
      </div>
    </SectionCard>
  );
}

/* -------- Vertical assignment popover -------- */
function VerticalPicker({ verticals, value, onChange }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" data-testid="assign-verticals-trigger">
          <Layers className="h-3.5 w-3.5" />
          {value.length ? `${value.length} vertical(s)` : "Assign verticals"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60">
        <p className="mb-2 text-xs font-semibold text-muted-foreground">Assigned Verticals</p>
        <div className="space-y-1.5">
          {verticals.map((v) => (
            <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={value.includes(v)} onCheckedChange={() => toggle(v)} data-testid={`assign-vertical-${v}`} />
              {v}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* -------- Vertical Lead access controls (dashboard + logs + strategy) -------- */
function VerticalLeadAccess() {
  const [access, setAccess] = useState(null);     // { show_strategy }
  const [vis, setVis] = useState(null);           // { total, hidden, visible, by_vertical: [] }
  const [busy, setBusy] = useState(false);

  const loadVis = async () => { try { setVis(await api.leadsLogVisibility()); } catch { /* ignore */ } };
  const loadAccess = async () => { try { setAccess(await api.leadAccess()); } catch { setAccess({ show_strategy: false }); } };
  useEffect(() => { loadAccess(); loadVis(); }, []);

  const toggleStrategy = async (v) => {
    setAccess((a) => ({ ...a, show_strategy: v }));
    try {
      const res = await api.saveLeadAccess({ show_strategy: v });
      setAccess(res);
      toast.success(v ? "Strategy & Governance now visible to Vertical Leads" : "Strategy & Governance hidden from Vertical Leads");
    } catch (e) { toast.error("Could not update setting"); loadAccess(); }
  };

  const toggleHideSpends = async (v) => {
    setAccess((a) => ({ ...a, hide_all_spends: v }));
    try {
      const res = await api.saveLeadAccess({ hide_all_spends: v });
      setAccess(res);
      toast.success(v ? "Vertical Leads now see only their own spends" : "Vertical Leads can see all vertical spends");
    } catch (e) { toast.error("Could not update setting"); loadAccess(); }
  };

  const runHide = async (vertical) => {
    setBusy(true);
    try {
      const res = await api.leadsHideLogs(vertical);
      setVis(res);
      toast.success(vertical ? `Existing ${vertical} logs hidden from Vertical Leads` : "All existing logs hidden from Vertical Leads");
    } catch (e) { toast.error("Action failed"); }
    finally { setBusy(false); }
  };
  const runShow = async (vertical) => {
    setBusy(true);
    try {
      const res = await api.leadsShowLogs(vertical);
      setVis(res);
      toast.success(vertical ? `${vertical} logs visible to Vertical Leads again` : "All logs visible to Vertical Leads again");
    } catch (e) { toast.error("Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="vertical-lead-access">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Dashboard access explainer + Strategy toggle */}
        <SectionCard title="Vertical Lead Experience" description="Control what limited-access Vertical Leads (Managers) can see.">
          <div className="space-y-3 p-4">
            <div className="flex items-start gap-2 rounded-lg border bg-[hsl(var(--primary)/0.06)] px-3 py-2 text-xs text-muted-foreground">
              <LayoutDashboard className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>Grant each Vertical Lead their monthly <b className="text-foreground">Dashboard</b> using the <b className="text-foreground">Dashboard</b> toggle in the Users table below. Off by default.</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2.5" data-testid="lead-strategy-toggle-row">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]"><Lightbulb className="h-4 w-4" /></span>
                <div>
                  <p className="text-sm font-medium">Strategy &amp; Governance tab</p>
                  <p className="text-[11px] text-muted-foreground">Allow Vertical Leads to view this tab in Marketing Plan.</p>
                </div>
              </div>
              {access ? (
                <Switch checked={!!access.show_strategy} onCheckedChange={toggleStrategy} data-testid="lead-strategy-toggle" />
              ) : <span className="text-[11px] text-muted-foreground">…</span>}
            </div>
            <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2.5" data-testid="lead-hidespends-toggle-row">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--primary)/0.12)] text-primary"><Wallet className="h-4 w-4" /></span>
                <div>
                  <p className="text-sm font-medium">Show only "My Spends" to Leads</p>
                  <p className="text-[11px] text-muted-foreground">Hide the full "All Spends" ledger; Leads see only spends they logged.</p>
                </div>
              </div>
              {access ? (
                <Switch checked={!!access.hide_all_spends} onCheckedChange={toggleHideSpends} data-testid="lead-hidespends-toggle" />
              ) : <span className="text-[11px] text-muted-foreground">…</span>}
            </div>
          </div>
        </SectionCard>

        {/* Log visibility summary + global actions */}
        <SectionCard title="Legacy Log Visibility"
          description="Hide past spend logs so Vertical Leads only see invoices logged from now onwards."
          action={vis ? <Badge variant="secondary" className="tabular-nums" data-testid="lead-logs-hidden-count">{vis.hidden} hidden</Badge> : null}>
          <div className="space-y-3 p-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border bg-[hsl(var(--surface-2))] p-2.5" data-testid="lead-logs-total"><p className="text-lg font-semibold tabular-nums">{vis ? vis.total : "—"}</p><p className="text-[11px] text-muted-foreground">Total logs</p></div>
              <div className="rounded-lg border bg-[hsl(var(--success)/0.08)] p-2.5" data-testid="lead-logs-visible"><p className="text-lg font-semibold tabular-nums text-[hsl(var(--success))]">{vis ? vis.visible : "—"}</p><p className="text-[11px] text-muted-foreground">Visible to leads</p></div>
              <div className="rounded-lg border bg-[hsl(var(--warning)/0.1)] p-2.5" data-testid="lead-logs-hidden"><p className="text-lg font-semibold tabular-nums text-[hsl(var(--warning))]">{vis ? vis.hidden : "—"}</p><p className="text-[11px] text-muted-foreground">Hidden</p></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="default" size="sm" className="h-9 gap-1.5" disabled={busy || !vis} data-testid="lead-logs-hide-all"><EyeOff className="h-4 w-4" /> Hide all past logs</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Hide all existing logs from Vertical Leads?</AlertDialogTitle>
                    <AlertDialogDescription>All {vis?.total ?? 0} current spend logs will be hidden from every Vertical Lead. They will only see invoices logged from now onwards. This is reversible.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="lead-logs-hide-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => runHide(null)} data-testid="lead-logs-hide-confirm">Hide all</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled={busy || !vis || vis.hidden === 0} onClick={() => runShow(null)} data-testid="lead-logs-show-all"><Eye className="h-4 w-4" /> Show all past logs</Button>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Per-vertical control */}
      <SectionCard title="Per-Vertical Log Visibility" description="Hide or reveal legacy logs for a single vertical.">
        <div className="table-scroll">
          <table className="w-full border-collapse" data-testid="lead-logs-vertical-table">
            <thead><tr>
              {["Vertical", "Total", "Visible", "Hidden", "Action"].map((h, i) => (
                <th key={h} className={cn("th-sticky px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground", i === 0 ? "th-sticky-left text-left" : i === 4 ? "text-right" : "text-right")}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {!vis ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : vis.by_vertical.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-foreground">No spend logs yet.</td></tr>
              ) : vis.by_vertical.map((b, i) => {
                const allHidden = b.hidden >= b.total && b.total > 0;
                return (
                  <tr key={b.vertical} className={cn("border-b last:border-0", i % 2 && "row-zebra")} data-testid={`lead-logs-vrow-${b.vertical.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                    <td className="td-sticky-left px-3 py-2.5 text-sm font-medium">{b.vertical}</td>
                    <td className="px-3 py-2.5 text-right text-sm tabular-nums text-muted-foreground">{b.total}</td>
                    <td className="px-3 py-2.5 text-right text-sm tabular-nums text-[hsl(var(--success))]">{b.visible}</td>
                    <td className="px-3 py-2.5 text-right text-sm tabular-nums text-[hsl(var(--warning))]">{b.hidden}</td>
                    <td className="px-3 py-2.5 text-right">
                      {allHidden ? (
                        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={busy} onClick={() => runShow(b.vertical)} data-testid={`lead-logs-show-${b.vertical.replace(/[^a-zA-Z0-9]+/g, "-")}`}><Eye className="h-3.5 w-3.5" /> Show</Button>
                      ) : (
                        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={busy || b.total === 0} onClick={() => runHide(b.vertical)} data-testid={`lead-logs-hide-${b.vertical.replace(/[^a-zA-Z0-9]+/g, "-")}`}><EyeOff className="h-3.5 w-3.5" /> Hide</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

/* -------- Users management -------- */
function UsersManager({ verticals }) {
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === "admin";
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "executive", assigned_verticals: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => setUsers(await api.listUsers());
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    setBusy(true);
    try {
      await api.createUser(form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "executive", assigned_verticals: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create user"); }
    finally { setBusy(false); }
  };

  const updateRole = async (u, role) => { await api.updateUser(u.id, { role }); toast.success("Role updated"); load(); };
  const updateVerticals = async (u, av) => { await api.updateUser(u.id, { assigned_verticals: av }); load(); };
  const updateDash = async (u, val) => {
    await api.updateUser(u.id, { dashboard_access: val });
    toast.success(val ? "Dashboard access granted" : "Dashboard access revoked");
    load();
  };
  const toggleActive = async (u) => { await api.updateUser(u.id, { active: !u.active }); toast.success(u.active ? "User deactivated" : "User activated"); load(); };
  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email}:`);
    if (!pw) return;
    await api.updateUser(u.id, { password: pw });
    toast.success("Password reset");
  };
  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await api.deleteUser(u.id); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  if (!users) return <div className="p-4"><Loading rows={3} /></div>;

  return (
    <div className="space-y-4">
      {isSuperAdmin && <VerticalLeadAccess />}

      <SectionCard title="Add User" description="Executives can enter spends & invoices. Managers are restricted to their assigned verticals. Leaders/Admins have full access.">
        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-6">
          <div><Label className="text-xs text-muted-foreground">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-9" data-testid="new-user-name" /></div>
          <div><Label className="text-xs text-muted-foreground">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-9" data-testid="new-user-email" /></div>
          <div><Label className="text-xs text-muted-foreground">Password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-9" data-testid="new-user-password" /></div>
          <div><Label className="text-xs text-muted-foreground">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1 h-9" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            {form.role === "manager"
              ? <VerticalPicker verticals={verticals} value={form.assigned_verticals} onChange={(av) => setForm({ ...form, assigned_verticals: av })} />
              : <span className="text-[11px] text-muted-foreground">All verticals</span>}
          </div>
          <div className="flex items-end"><Button onClick={create} disabled={busy} className="h-9 w-full" data-testid="create-user-button"><UserPlus className="mr-1.5 h-4 w-4" />Create</Button></div>
        </div>
      </SectionCard>

      <SectionCard title="Users" description="Managers (Vertical Leads) see a monthly dashboard only when Dashboard access is granted below.">
        <div className="table-scroll">
          <table className="w-full border-collapse" data-testid="users-table">
            <thead><tr>
              {["Name", "Email", "Role", "Verticals", "Dashboard", "Active", "Actions"].map((c) => <th key={c} className="th-sticky px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c}</th>)}
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-[hsl(var(--primary)/0.05)]">
                  <td className="px-3 py-2.5 text-sm">{u.name}{u.id === me.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</td>
                  <td className="px-3 py-2.5 text-sm">{u.email}</td>
                  <td className="px-3 py-2.5">
                    <Select value={u.role} onValueChange={(v) => updateRole(u, v)} disabled={u.id === me.id}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2.5">
                    {u.role === "manager"
                      ? <VerticalPicker verticals={verticals} value={u.assigned_verticals || []} onChange={(av) => updateVerticals(u, av)} />
                      : <span className="text-[11px] text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    {u.role === "manager"
                      ? (isSuperAdmin
                        ? (
                          <div className="flex items-center gap-2">
                            <Switch checked={!!u.dashboard_access} onCheckedChange={(v) => updateDash(u, v)} data-testid={`user-dashboard-${u.email}`} />
                            <span className="text-[11px] text-muted-foreground">{u.dashboard_access ? "On" : "Off"}</span>
                          </div>
                        )
                        : <span className="text-[11px] text-muted-foreground">{u.dashboard_access ? "On" : "Off"}</span>)
                      : <span className="text-[11px] text-muted-foreground">Full</span>}
                  </td>
                  <td className="px-3 py-2.5"><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={u.id === me.id} /></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => resetPw(u)} aria-label="Reset password"><KeyRound className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--danger))]" onClick={() => del(u)} disabled={u.id === me.id} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

/* -------- Master lists tab -------- */
function MasterLists() {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  // Mirror draft in a ref so Save reads the freshest state — including a value the
  // user typed into an "Add" box but hadn't clicked "+" for (committed on blur).
  const draftRef = useRef(null);
  draftRef.current = draft;

  const load = async () => { const l = await api.lists(); setDraft(JSON.parse(JSON.stringify(l))); };
  useEffect(() => { load(); }, []);

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    const d = draftRef.current || draft;
    if (!d) return;
    setSaving(true);
    try {
      await api.updateLists({
        projects: d.projects, verticals: d.verticals, vis_perf: d.vis_perf,
        vendors: d.vendors, portfolios: d.portfolios, platforms: d.platforms,
        activities: d.activities, cost_types: d.cost_types, outcomes: d.outcomes,
        payment_methods: d.payment_methods, employees: d.employees,
        billing_businesses: d.billing_businesses,
      });
      toast.success("Master lists saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save lists"); }
    finally { setSaving(false); }
  };

  if (!draft) return <div className="p-4"><Loading rows={3} /></div>;

  const DERIVED_NOTE = "Auto-populated from your uploaded data. Add extra items here — they merge on top and stay in sync as you edit the Data Manager.";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2 rounded-lg border bg-[hsl(var(--primary)/0.06)] px-3 py-2 text-xs text-muted-foreground" data-testid="lists-auto-note">
          <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>These lists are <b className="text-foreground">auto-populated</b> from the imported spends &amp; funnel data and stay <b className="text-foreground">live-synced</b> with the Data Manager. Manually added items are merged on top.</span>
        </div>
        <Button onClick={save} disabled={saving} data-testid="lists-save-button" className="shrink-0">
          <Save className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save Master Lists"}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <MappedListEditor title="Projects" description="Each project maps to a portfolio. New projects from imported data appear automatically." items={draft.projects} onChange={(v) => set("projects", v)} mapKey="portfolio" mapLabel="Portfolio" options={draft.portfolios || []} testid="projects-editor" />
        <StringListEditor title="Verticals" description="Marketing verticals used across the dashboard." items={draft.verticals} onChange={(v) => set("verticals", v)} testid="verticals-editor" note={DERIVED_NOTE} />
        <StringListEditor title="Vendors" description="Every vendor found in the spend ledger." items={draft.vendors} onChange={(v) => set("vendors", v)} testid="vendors-editor" note={DERIVED_NOTE} />
        <StringListEditor title="Billing Businesses" description="Legal billing entities selectable on each proforma invoice." items={draft.billing_businesses} onChange={(v) => set("billing_businesses", v)} testid="billing-businesses-editor" />
        <StringListEditor title="Platforms" description="Media / channel platforms from the spend data." items={draft.platforms} onChange={(v) => set("platforms", v)} testid="platforms-editor" note={DERIVED_NOTE} />
        <StringListEditor title="Activities" description="Spend line-item activities from the imported data." items={draft.activities} onChange={(v) => set("activities", v)} testid="activities-editor" note={DERIVED_NOTE} />
        <StringListEditor title="Cost / Department Types" description="Cost or department classifications." items={draft.cost_types} onChange={(v) => set("cost_types", v)} testid="costtypes-editor" note={DERIVED_NOTE} />
        <StringListEditor title="Outcomes" description="Spend outcome / objective tags." items={draft.outcomes} onChange={(v) => set("outcomes", v)} testid="outcomes-editor" note={DERIVED_NOTE} />
        <StringListEditor title="Visibility / Performance" description="Spend-type splits (e.g. Visibility, Performance)." items={draft.vis_perf} onChange={(v) => set("vis_perf", v)} testid="visperf-editor" />
        <StringListEditor title="Portfolios" description="Portfolio groupings for projects." items={draft.portfolios} onChange={(v) => set("portfolios", v)} testid="portfolios-editor" />
        <StringListEditor title="Payment Methods" items={draft.payment_methods} onChange={(v) => set("payment_methods", v)} testid="payment-methods-editor" />
        <StringListEditor title="Logged By (Employees)" items={draft.employees} onChange={(v) => set("employees", v)} testid="employees-editor" />
      </div>
    </div>
  );
}

/* -------- Dashboard Layout builder (Phase J) -------- */
const GRAN_OPTIONS = ["Monthly", "Quarterly", "Yearly"];
const FILTER_TOGGLES = [
  { key: "show_region", label: "Region quick-filter chips" },
  { key: "show_category", label: "Category quick-filter chips" },
  { key: "show_team", label: "Team quick-filter chips" },
];

function DashboardLayoutBuilder() {
  const [sections, setSections] = useState(null);
  const [filters, setFilters] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const cfg = await api.dashboardConfig();
    setSections((cfg.sections || []).map((s) => ({ ...s, visible: s.visible !== false })));
    setFilters({
      show_region: cfg.filters?.show_region !== false,
      show_category: cfg.filters?.show_category !== false,
      show_team: !!cfg.filters?.show_team,
      granularities: (cfg.filters?.granularities && cfg.filters.granularities.length) ? cfg.filters.granularities : [...GRAN_OPTIONS],
    });
  };
  useEffect(() => { load(); }, []);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(sections);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setSections(items);
  };
  const toggleVisible = (key) => setSections((arr) => arr.map((s) => s.key === key ? { ...s, visible: !s.visible } : s));

  const save = async () => {
    setSaving(true);
    try {
      await api.saveDashboardConfig({ sections, filters });
      toast.success("Dashboard layout saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save layout"); }
    finally { setSaving(false); }
  };
  const resetDefaults = () => {
    setSections((arr) => arr.map((s) => ({ ...s, visible: true })));
    setFilters({ show_region: true, show_category: true, show_team: false, granularities: [...GRAN_OPTIONS] });
    toast.message("Reset to defaults — remember to Save");
  };

  if (!sections || !filters) return <div className="p-4"><Loading rows={3} /></div>;

  const visibleCount = sections.filter((s) => s.visible).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground" data-testid="dashboard-layout-summary">
          {visibleCount} of {sections.length} sections visible · drag to reorder
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={resetDefaults} className="h-9" data-testid="dashboard-layout-reset"><RotateCcw className="mr-1.5 h-4 w-4" /> Reset</Button>
          <Button onClick={save} disabled={saving} className="h-9" data-testid="dashboard-layout-save"><Save className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save Layout"}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <SectionCard title="Dashboard Sections" description="Drag to reorder. Toggle to show or hide on the Dashboard." className="lg:col-span-2">
          <div className="p-3">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="dashboard-sections">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2" data-testid="dashboard-sections-list">
                    {sections.map((s, index) => (
                      <Draggable key={s.key} draggableId={s.key} index={index}>
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            data-testid={`dashboard-section-item-${s.key}`}
                            className={cn(
                              "flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 transition-shadow duration-150",
                              snapshot.isDragging && "shadow-lg ring-2 ring-[hsl(var(--ring))]",
                              !s.visible && "opacity-60"
                            )}
                          >
                            <button {...dragProvided.dragHandleProps} className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing" aria-label="Drag to reorder" data-testid={`dashboard-section-handle-${s.key}`}>
                              <GripVertical className="h-4 w-4" />
                            </button>
                            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary text-muted-foreground"><LayoutGrid className="h-3.5 w-3.5" /></span>
                            <span className="flex-1 text-sm font-medium">{s.label || s.key}</span>
                            <span className="text-[11px] text-muted-foreground">{s.visible ? "Visible" : "Hidden"}</span>
                            <Switch checked={s.visible} onCheckedChange={() => toggleVisible(s.key)} data-testid={`dashboard-section-toggle-${s.key}`} />
                            {s.visible ? <Eye className="h-4 w-4 text-[hsl(var(--success))]" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </SectionCard>

        <SectionCard title="Filter Bar" description="Configure the global filter chips and period options.">
          <div className="space-y-4 p-4">
            <div className="space-y-2.5" data-testid="dashboard-filter-toggles">
              {FILTER_TOGGLES.map((f) => (
                <div key={f.key} className="flex items-center justify-between rounded-lg border bg-card px-3 py-2">
                  <span className="text-sm">{f.label}</span>
                  <Switch checked={!!filters[f.key]} onCheckedChange={(v) => setFilters((fl) => ({ ...fl, [f.key]: v }))} data-testid={`dashboard-filter-${f.key}`} />
                </div>
              ))}
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Period granularities</p>
              <ToggleGroup type="multiple" value={filters.granularities}
                onValueChange={(v) => setFilters((fl) => ({ ...fl, granularities: v.length ? v : fl.granularities }))}
                className="flex flex-wrap gap-1.5" data-testid="dashboard-granularities-toggle">
                {GRAN_OPTIONS.map((g) => (
                  <ToggleGroupItem key={g} value={g} className="h-8 rounded-md border px-3 text-xs data-[state=on]:border-primary data-[state=on]:bg-[hsl(var(--primary)/0.12)] data-[state=on]:text-primary" data-testid={`dashboard-gran-option-${g}`}>{g}</ToggleGroupItem>
                ))}
              </ToggleGroup>
              <p className="mt-1.5 text-[11px] text-muted-foreground">At least one granularity must stay enabled.</p>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

/* -------- Alert Thresholds (per-vertical overspend warning levels) -------- */
function AlertThresholds() {
  const [def, setDef] = useState("85");
  const [byV, setByV] = useState({});
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.alertThresholds();
      setVerticals(d.verticals || []);
      setDef(String(Math.round((d.default_threshold || 0.85) * 100)));
      const bv = {};
      Object.entries(d.by_vertical || {}).forEach(([k, v]) => { bv[k] = String(Math.round(v * 100)); });
      setByV(bv);
    } catch (e) { toast.error("Could not load alert thresholds"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setV = (v, val) => setByV((s) => ({ ...s, [v]: val }));
  const clearV = (v) => setByV((s) => { const n = { ...s }; delete n[v]; return n; });

  const save = async () => {
    setSaving(true);
    try {
      const default_threshold = Math.min(1.5, Math.max(0.1, (Number(def) || 85) / 100));
      const by_vertical = {};
      Object.entries(byV).forEach(([k, v]) => {
        const num = Number(v);
        if (v !== "" && !Number.isNaN(num)) by_vertical[k] = Math.min(1.5, Math.max(0.1, num / 100));
      });
      await api.saveAlertThresholds({ default_threshold, by_vertical });
      toast.success("Alert thresholds saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save thresholds"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-4"><Loading rows={3} /></div>;

  return (
    <div className="space-y-4" data-testid="alert-thresholds-form">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2 rounded-lg border bg-[hsl(var(--warning)/0.08)] px-3 py-2 text-xs text-muted-foreground">
          <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
          <span>Set the <b className="text-foreground">warning level</b> at which each vertical is flagged as <b className="text-foreground">nearing its budget cap</b>. A vertical always shows an <b className="text-foreground">overspend</b> alert once actual spend passes 100% of its cap. Verticals left blank use the default level below.</span>
        </div>
        <Button onClick={save} disabled={saving} data-testid="alert-thresholds-save" className="shrink-0">
          <Save className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save Thresholds"}
        </Button>
      </div>

      <SectionCard title="Default Warning Level" description="Applied to any vertical without a custom level.">
        <div className="flex items-center gap-3 p-4">
          <div className="relative w-40">
            <Input type="number" min="10" max="150" step="1" value={def} onChange={(e) => setDef(e.target.value)}
              className="h-10 pr-8 text-lg font-semibold tabular-nums" data-testid="alert-default-input" />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
          </div>
          <p className="text-xs text-muted-foreground">Warn when a vertical's monthly spend reaches this share of its planned cap.</p>
        </div>
      </SectionCard>

      <SectionCard title="Per-Vertical Warning Levels" description="Override the default for specific budget verticals.">
        <div className="table-scroll">
          <table className="w-full border-collapse" data-testid="alert-vertical-table">
            <thead><tr>
              {["Budget Vertical", "Warning Level", "Effective", ""].map((h, i) => (
                <th key={h + i} className={cn("th-sticky px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground", i === 0 ? "th-sticky-left text-left" : i === 3 ? "text-right" : "text-left")}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {verticals.map((v, i) => {
                const custom = byV[v] !== undefined && byV[v] !== "";
                const effective = custom ? byV[v] : def;
                return (
                  <tr key={v} className={cn("border-b last:border-0", i % 2 && "row-zebra")} data-testid={`alert-vertical-row-${i}`}>
                    <td className="td-sticky-left px-3 py-2.5 text-sm font-medium">{v}</td>
                    <td className="px-3 py-2.5">
                      <div className="relative w-32">
                        <Input type="number" min="10" max="150" step="1" value={byV[v] ?? ""} placeholder={def}
                          onChange={(e) => setV(v, e.target.value)} className="h-9 pr-7 tabular-nums" data-testid={`alert-vertical-input-${v.replace(/[^a-zA-Z0-9]+/g, "-")}`} />
                        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={cn("rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
                        custom ? "bg-[hsl(var(--primary)/0.12)] text-primary" : "bg-secondary text-muted-foreground")}>
                        {effective}%{custom ? "" : " · default"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {custom && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => clearV(v)} data-testid={`alert-vertical-reset-${i}`}>
                          <RotateCcw className="mr-1 h-3 w-3" /> Use default
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}


export default function Settings() {
  const [verticals, setVerticals] = useState([]);
  useEffect(() => { api.lists().then((l) => setVerticals(l.verticals || [])); }, []);
  return (
    <div className="space-y-5">
      <PageHeader title="Settings" description="Manage team members, role-based access and master lists. Leaders & Admins only." />
      <Tabs defaultValue="data" className="w-full">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="data" data-testid="tab-data">Data Manager</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users & Access</TabsTrigger>
          <TabsTrigger value="lists" data-testid="tab-lists">Master Lists</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Category Manager</TabsTrigger>
          <TabsTrigger value="layout" data-testid="tab-layout">Dashboard Layout</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Alert Thresholds</TabsTrigger>
        </TabsList>
        <TabsContent value="data" className="mt-4"><DataManager /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersManager verticals={verticals} /></TabsContent>
        <TabsContent value="lists" className="mt-4"><MasterLists /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoryManager /></TabsContent>
        <TabsContent value="layout" className="mt-4"><DashboardLayoutBuilder /></TabsContent>
        <TabsContent value="alerts" className="mt-4"><AlertThresholds /></TabsContent>
      </Tabs>
    </div>
  );
}
